// frontend/src/pages/MFALogin.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { mfaAuthenticate } from "../library/allauth";
import XRoadz_Logo from "../img/XRoadz_Logo.png";
export default function MFALogin() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const content = await mfaAuthenticate(code);
      if (content.status === 200 && content.meta?.is_authenticated) {
        setUser(content.data.user);
        navigate("/home");
      } else {
        setError(content.errors?.[0]?.message || "Invalid code. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = () => {
    setUseRecovery(!useRecovery);
    setCode("");
    setError("");
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.25; transform: scale(1.05); }
        }
        .pulse { animation: pulse-slow 4s ease-in-out infinite; }
      `}</style>

      <div className="min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex w-1/2 flex-col justify-between p-14 pt-20 relative overflow-hidden bg-emerald-700">
          <div className="pulse absolute w-[500px] h-[500px] rounded-full bg-emerald-600 opacity-20 -top-32 -left-32" />
          <div className="pulse absolute w-[300px] h-[300px] rounded-full bg-emerald-900 opacity-20 bottom-20 right-0" style={{ animationDelay: "2s" }} />

          {/* Logo */}
          <div className="relative z-10 flex items-center gap-4 mt-6">
            <div className="h-12 bg-white rounded-xl flex items-center justify-center shadow-sm px-3 overflow-hidden">
              <img src={XRoadz_Logo} alt="XRoadz" className="h-7 object-contain" />
            </div>
            <div>
              <p className="text-white font-bold text-xl leading-tight">XRoadz WebPortal</p>
              <p className="text-emerald-200 text-xs opacity-70 tracking-widest uppercase">
                Traffic Analytics & Roadway Safety
              </p>
            </div>
          </div>

          {/* Center content */}
          <div className="relative z-10 max-w-md">
            <h1 className="text-white text-4xl font-black leading-tight mb-4 tracking-tight">
              Verify<br />
              <span className="text-emerald-200">Your Identity.</span>
            </h1>
            <p className="text-emerald-100 text-sm leading-relaxed opacity-80">
              Two-factor authentication keeps your account secure even if your password is compromised.
            </p>
          </div>

          <p className="relative z-10 text-emerald-200 text-xs tracking-widest uppercase opacity-50">
            © {new Date().getFullYear()} XRoadz Inc. — Innovating Traffic Safety
          </p>
        </div>

        {/* Right Panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-8 py-12">
          <div className="w-full max-w-lg fade-up">

            {/* Mobile logo */}
            <div className="flex lg:hidden justify-center mb-8">
              <img src={XRoadz_Logo} alt="XRoadz" className="h-10 object-contain" />
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-10 shadow-sm">

              {/* Icon */}
              <div className="flex flex-col items-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4 border border-emerald-100">
                  {useRecovery ? (
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-emerald-600">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  ) : (
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-emerald-600">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {useRecovery ? "Recovery Code" : "Two-Factor Authentication"}
                </h2>
                <p className="text-sm text-gray-400 text-center">
                  {useRecovery
                    ? "Enter one of your backup recovery codes"
                    : "Enter the 6-digit code from your authenticator app"}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    {useRecovery ? "Recovery Code" : "Authentication Code"}
                  </label>
                  <input
                    type="text"
                    inputMode={useRecovery ? "text" : "numeric"}
                    maxLength={useRecovery ? 8 : 6}
                    placeholder={useRecovery ? "xxxxxxxx" : "000000"}
                    value={code}
                    onChange={(e) => {
                      const val = useRecovery
                        ? e.target.value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()
                        : e.target.value.replace(/\D/g, "");
                      setCode(val);
                    }}
                    className={`w-full px-4 py-4 text-center font-mono bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-300 transition-colors ${
                      useRecovery ? "text-xl tracking-[0.3em]" : "text-3xl tracking-[0.5em]"
                    }`}
                    autoFocus
                  />
                  {error && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                      <p className="text-xs text-red-600 text-center">{error}</p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || (useRecovery ? code.length !== 8 : code.length !== 6)}
                  className="w-full py-3 px-4 text-sm font-semibold text-white bg-emerald-700 rounded-xl hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : "Verify Code"}
                </button>

                {/* Toggle between TOTP and recovery code */}
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={handleModeSwitch}
                    className="text-sm text-emerald-700 hover:text-emerald-800 hover:underline transition-colors"
                  >
                    {useRecovery
                      ? "← Use authenticator app instead"
                      : "Lost access to your app? Use a recovery code"}
                  </button>
                </div>

                {!useRecovery && (
                  <p className="text-center text-xs text-gray-400">
                    No recovery codes?{" "}
                    <a href="/support" className="text-emerald-700 hover:underline">
                      Contact support
                    </a>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}